import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import VisuallyHidden from 'components/VisuallyHidden'
import { ACCENT, BORDER, INK, MUTED } from 'features/actions/workspace/ui'

const COLUMNS = [
  { key: 'documents', label: 'Documents' },
  { key: 'approvers', label: 'Approvers' },
]

const TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'domain', label: 'Domains' },
  { key: 'sharepoint', label: 'SharePoint' },
]

const WHO_FILTERS = [
  { key: 'all', label: 'Everyone' },
  { key: 'default', label: 'Uses Super Admins' },
  { key: 'named', label: 'Named approvers' },
]

const buildRoutes = (domains, sharepoint) => [
  ...domains.map(d => ({
    id: `domain:${d.domain}`,
    kind: 'domain',
    title: `@${d.domain} uploads`,
    detail: null,
    searchText: d.domain,
    usesDefault: !!d.usesDefault || (d.approvers || []).length === 0,
    approvers: d.approvers || [],
    edit: { kind: 'domain', domains: [d.domain], approvers: d.approvers || [] },
  })),
  ...sharepoint.map(s => ({
    id: `sharepoint:${s.customViewId}`,
    kind: 'sharepoint',
    title: `SharePoint: ${s.name}`,
    detail: s.building?.name || null,
    searchText: `${s.name} ${s.building?.name || ''}`,
    usesDefault: !!s.usesDefault || (s.approvers || []).length === 0,
    approvers: s.approvers || [],
    edit: { kind: 'sharepoint', customViewId: s.customViewId, buildingPassportId: s.building?.id, name: s.name, approvers: s.approvers || [] },
  })),
]

const approverNames = r => r.approvers.map(a => a.name).join(', ').toLowerCase()

const ApproversTable = ({ domains = [], sharepoint = [], onEdit }) => {
  const [q, setQ] = useState('')
  const [type, setType] = useState('all')
  const [who, setWho] = useState('all')
  const [sort, setSort] = useState({ key: 'documents', dir: 'asc' })

  const routes = useMemo(() => buildRoutes(domains, sharepoint), [domains, sharepoint])

  const visible = useMemo(() => {
    const term = q.trim().toLowerCase()
    const filtered = routes
      .filter(r => type === 'all' || r.kind === type)
      .filter(r => who === 'all' || (who === 'default' ? r.usesDefault : !r.usesDefault))
      .filter(r => !term || `${r.searchText} ${approverNames(r)}`.toLowerCase().includes(term))
    const value = r => (sort.key === 'approvers' ? (r.usesDefault ? '' : approverNames(r)) : r.title.toLowerCase())
    return [...filtered].sort((a, b) => (value(a) > value(b) ? 1 : value(a) < value(b) ? -1 : 0) * (sort.dir === 'asc' ? 1 : -1))
  }, [routes, q, type, who, sort])

  const toggleSort = key => setSort(s => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))

  return (
    <Wrapper>
      <Controls>
        <Search aria-label='Search approvers' placeholder='Search by domain, building or approver' value={q} onChange={e => setQ(e.target.value)} />
        <Chips role='group' aria-label='Filter by type of documents'>
          {TYPE_FILTERS.map(f => (
            <Chip key={f.key} type='button' aria-pressed={type === f.key} $on={type === f.key} onClick={() => setType(f.key)}>{f.label}</Chip>
          ))}
        </Chips>
        <Chips role='group' aria-label='Filter by who approves'>
          {WHO_FILTERS.map(f => (
            <Chip key={f.key} type='button' aria-pressed={who === f.key} $on={who === f.key} onClick={() => setWho(f.key)}>{f.label}</Chip>
          ))}
        </Chips>
      </Controls>
      <Count>Showing {visible.length} of {routes.length} routes</Count>
      {visible.length === 0 ? (
        <Empty>No routes match. Try a different search or filter.</Empty>
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  <th key={c.key} aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    <SortButton type='button' onClick={() => toggleSort(c.key)}>
                      {c.label} {sort.key === c.key ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
                    </SortButton>
                  </th>
                ))}
                <th><VisuallyHidden>Actions</VisuallyHidden></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, index) => (
                <tr key={r.id}>
                  <td>
                    <Strong>{r.title}</Strong>
                    {r.detail && <Sub>{r.detail}</Sub>}
                  </td>
                  <td>
                    {r.usesDefault ? (
                      <Strong>Super Admins (default)</Strong>
                    ) : (
                      r.approvers.map(a => (
                        <Person key={a.id}>
                          <Strong>{a.name}</Strong>
                          {a.isSuperAdmin && <Tag>Super Admin</Tag>}
                          {a.eligible === false && (
                            <Warn>
                              {a.name} can&apos;t approve requests for your organisation, so the Super Admins still decide them.
                            </Warn>
                          )}
                        </Person>
                      ))
                    )}
                  </td>
                  <td>
                    <EditButton type='button' data-tour-id={index === 0 ? 'organisation.approvers-edit' : undefined} aria-label={`Change approvers for ${r.title}`} onClick={() => onEdit?.(r.edit)}>
                      Change approvers
                    </EditButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}
    </Wrapper>
  )
}

export default ApproversTable

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
`

const Search = styled.input`
  flex: 1 1 260px;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  font-size: 15px;
  font-family: inherit;
  max-width: 360px;

  &:focus { outline: 2px solid ${ACCENT}; outline-offset: 1px; }

  @media (max-width: 768px) {
    max-width: none;
    flex-basis: 100%;
  }
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Chip = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px solid ${({ $on }) => ($on ? ACCENT : BORDER)};
  background: ${({ $on }) => ($on ? '#fff' : '#fafafa')};
  color: ${({ $on }) => ($on ? INK : MUTED)};
  font: inherit;
  font-size: 15px;
  font-weight: ${({ $on }) => ($on ? 700 : 600)};
  cursor: pointer;
`

const Count = styled.div`
  font-size: 15px;
  color: ${MUTED};
`

const Empty = styled.p`
  margin: 0;
  font-size: 15px;
  color: ${MUTED};
`

const TableScroll = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;

  th, td { text-align: left; padding: 12px 10px; border-bottom: 1px solid ${BORDER}; vertical-align: top; }

  @media (max-width: 768px) {
    th, td { padding: 10px 6px; }
  }
`

const SortButton = styled.button`
  border: 0;
  background: transparent;
  font: inherit;
  font-weight: 700;
  font-size: 15px;
  color: ${MUTED};
  cursor: pointer;
  min-height: 40px;
  padding: 0;

  &:hover { color: ${INK}; }
`

const Strong = styled.div`
  font-weight: 700;
  color: ${INK};
  overflow-wrap: anywhere;
`

const Sub = styled.div`
  font-size: 15px;
  color: ${MUTED};
`

const Person = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 6px;
`

const Tag = styled.span`
  align-self: flex-start;
  font-size: 14px;
  color: ${MUTED};
`

const Warn = styled.div`
  font-size: 15px;
  color: #c2410c;
`

const EditButton = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: #fff;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:hover { border-color: ${ACCENT}; }
`
